import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { Mail, MapPin } from 'lucide-react';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import './ContactManagement.css';
import './ModuleAdmin.css';

const ContactManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const { showToast } = useToast();

  const { data: officesData, isLoading: officesLoading } = useQuery('contact-offices', adminAPI.getContactOffices);
  const { data: inquiriesData, isLoading: inquiriesLoading } = useQuery(
    ['contact-inquiries', statusFilter],
    () => adminAPI.getContactInquiries({ status: statusFilter || undefined })
  );

  const offices = officesData?.data?.data || [];
  const inquiries = inquiriesData?.data?.data?.inquiries || [];

  const deleteOfficeMutation = useMutation(
    (id) => adminAPI.deleteContactOffice(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contact-offices');
        showToast({ message: 'Office deleted.', severity: 'success' });
      },
      onError: (err) =>
        showToast({ message: err.response?.data?.message || 'Failed to delete office.', severity: 'error' })
    }
  );

  const updateInquiryMutation = useMutation(
    ({ id, status }) => adminAPI.updateContactInquiryStatus(id, status, 'admin-panel'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contact-inquiries');
        showToast({ message: 'Inquiry status updated.', severity: 'success' });
      },
      onError: (err) =>
        showToast({ message: err.response?.data?.message || 'Failed to update inquiry status.', severity: 'error' })
    }
  );

  return (
    <div className="contact-admin-page module-page">
      <PageHeader
        title="Get In Touch Management"
        subtitle="Manage office cards and handle incoming contact form submissions."
      />

      <Card sx={{ mb: 2.5 }}>
        <CardContent>
          <div className="contact-offices-section-head">
            <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapPin size={18} /> Offices
            </Typography>
            <Button component={Link} to="/contact/offices/new" variant="contained" startIcon={<AddRoundedIcon />}>
              Add Office
            </Button>
          </div>
          {officesLoading ? (
            <div className="ops-loading-box">
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading offices…</Typography>
            </div>
          ) : offices.length === 0 ? (
            <EmptyState
              title="No offices yet"
              description="Add regional office cards shown on the contact page."
              actionLabel="Add Office"
              onAction={() => navigate('/contact/offices/new')}
            />
          ) : (
            <div className="contact-office-list">
              {offices.map((office) => (
                <div key={office.id} className="contact-office-item">
                  <div>
                    <strong>{office.officeName || office.city}</strong>
                    <p>{office.address}</p>
                    <small>{office.phonePrimary || 'No phone'} {office.timings ? `• ${office.timings}` : ''}</small>
                    {office.isActive === false ? (
                      <Box sx={{ mt: 0.75 }}>
                        <StatusChip status="inactive" label="Inactive" />
                      </Box>
                    ) : null}
                  </div>
                  <div className="contact-item-actions">
                    <Button
                      component={Link}
                      to={`/contact/offices/${office.id}/edit`}
                      variant="outlined"
                      size="small"
                      startIcon={<EditRoundedIcon fontSize="small" />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteRoundedIcon fontSize="small" />}
                      onClick={() => deleteOfficeMutation.mutate(office.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <FilterBar contentSx={{ py: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="contact-inquiry-status-label">Inquiry status</InputLabel>
          <Select
            labelId="contact-inquiry-status-label"
            label="Inquiry status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Mail size={18} /> Contact Inquiries
          </Typography>
          {inquiriesLoading ? (
            <div className="ops-loading-box">
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading inquiries…</Typography>
            </div>
          ) : inquiries.length === 0 ? (
            <EmptyState title="No inquiries yet" description="Contact form submissions will appear here." />
          ) : (
            <div className="contact-inquiry-list">
              {inquiries.map((inq) => (
                <div key={inq.id} className="contact-inquiry-item">
                  <div>
                    <strong>{inq.fullName}</strong>{' '}
                    <span className="contact-inquiry-email">{inq.email}</span>
                    <Box sx={{ my: 0.75 }}>
                      <StatusChip status={inq.status || 'new'} label={String(inq.status || 'new').replace(/_/g, ' ')} />
                    </Box>
                    <p className="contact-inquiry-subject">{inq.subject}</p>
                    <p>{inq.message}</p>
                  </div>
                  <div className="contact-item-actions">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id={`inq-status-${inq.id}`}>Status</InputLabel>
                      <Select
                        labelId={`inq-status-${inq.id}`}
                        label="Status"
                        value={inq.status}
                        onChange={(e) => updateInquiryMutation.mutate({ id: inq.id, status: e.target.value })}
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="in_progress">In progress</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactManagement;
